/**
 * Approvals Service
 * 
 * Handles post approval workflows including submission, approval, and rejection.
 * Integrates with activity logging for audit trail.
 */

import { supabase } from "@/integrations/supabase/client";
import { withServiceErrorHandling } from "@/lib/service-error";
import { logger } from "@/lib/structured-logger";
import { ActivityService } from "./activity.service";
import type { ApprovalQueueItem, ApprovalStats, ApprovalAction } from "@/types/approvals";

export class ApprovalsService {
  private static readonly logger = logger.child({ service: 'ApprovalsService' });

  /**
   * Submit a post for approval
   */
  static async submitForApproval(postId: string): Promise<void> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Submitting post for approval', { postId });
      const { data: post, error: fetchError } = await supabase
        .from("posts")
        .select("organization_id, created_by")
        .eq("id", postId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from("posts")
        .update({
          approval_status: "pending",
          submitted_for_approval_at: new Date().toISOString(),
        })
        .eq("id", postId);

      if (error) throw error;

      // Log activity
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await ActivityService.logActivity({
          userId: user.id,
          organizationId: post.organization_id,
          actionType: "post_submitted",
          entityType: "post",
          entityId: postId,
          metadata: { submitted_at: new Date().toISOString() },
        });
      }

      this.logger.info('Post submitted for approval', { postId });
    }, { operation: 'submitForApproval', table: 'posts' });
  }

  /**
   * Approve a post
   */
  static async approvePost(postId: string): Promise<void> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Approving post', { postId });

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: post, error: fetchError } = await supabase
        .from("posts")
        .select("organization_id, created_by")
        .eq("id", postId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from("posts")
        .update({
          approval_status: "approved",
          approved_by: user.id,
          approved_at: new Date().toISOString(),
          rejection_reason: null,
        })
        .eq("id", postId);

      if (error) throw error;

      // Log activity
      await ActivityService.logActivity({
        userId: user.id,
        organizationId: post.organization_id,
        actionType: "post_approved",
        entityType: "post",
        entityId: postId,
        metadata: { 
          approved_by: user.id,
          approved_at: new Date().toISOString() 
        },
      });

      // Trigger notification to post creator
      try {
        await supabase.functions.invoke("send-approval-notification", {
          body: {
            postId,
            status: "approved",
            userId: post.created_by,
          },
        });
      } catch (notifError) {
        this.logger.warn("Failed to send approval notification", { error: notifError, postId });
        // Don't throw - notification failure shouldn't block approval
      }

      this.logger.info('Post approved', { postId });
    }, { operation: 'approvePost', table: 'posts' });
  }

  /**
   * Reject a post with reason
   */
  static async rejectPost(
    postId: string,
    reason: string
  ): Promise<void> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Rejecting post', { postId, reason });

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: post, error: fetchError } = await supabase
        .from("posts")
        .select("organization_id, created_by")
        .eq("id", postId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from("posts")
        .update({
          approval_status: "rejected",
          rejection_reason: reason,
          submitted_for_approval_at: null,
        })
        .eq("id", postId);

      if (error) throw error;

      // Log activity
      await ActivityService.logActivity({
        userId: user.id,
        organizationId: post.organization_id,
        actionType: "post_rejected",
        entityType: "post",
        entityId: postId,
        metadata: { 
          rejected_by: user.id,
          reason,
          rejected_at: new Date().toISOString() 
        },
      });

      // Trigger notification to post creator
      try {
        await supabase.functions.invoke("send-approval-notification", {
          body: {
            postId,
            status: "rejected",
            reason,
            userId: post.created_by,
          },
        });
      } catch (notifError) {
        this.logger.warn("Failed to send rejection notification", { error: notifError, postId });
      }

      this.logger.info('Post rejected', { postId });
    }, { operation: 'rejectPost', table: 'posts' });
  }

  static async getApprovalQueue(
    organizationId: string
  ): Promise<ApprovalQueueItem[]> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching approval queue', { organizationId });
      const { data, error } = await supabase
        .from("posts")
        .select(`
          id,
          content,
          platform,
          scheduled_for,
          submitted_for_approval_at,
          created_at,
          organization_id
        `)
        .eq("organization_id", organizationId)
        .eq("approval_status", "pending")
        .order("submitted_for_approval_at", { ascending: false });

      if (error) throw error;
      if (!data) return [];

      const queue = data.map(post => ({
        id: post.id,
        content: post.content,
        platform: post.platform,
        scheduled_for: post.scheduled_for,
        submitted_for_approval_at: post.submitted_for_approval_at || '',
        created_at: post.created_at || '',
        organization_id: post.organization_id,
        created_by: null,
        profiles: null
      }));

      this.logger.info('Approval queue fetched', { 
        organizationId, 
        count: queue.length 
      });

      return queue as ApprovalQueueItem[];
    }, { operation: 'getApprovalQueue', table: 'posts' });
  }

  /**
   * Get approval statistics for organization
   */
  static async getApprovalStats(organizationId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching approval stats', { organizationId });
      const { count: pendingCount } = await supabase
        .from("posts")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", organizationId)
        .eq("approval_status", "pending");

      const { count: approvedToday } = await supabase
        .from("posts")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", organizationId)
        .eq("approval_status", "approved")
        .gte("approved_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

      const { count: rejectedToday } = await supabase
        .from("posts")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", organizationId)
        .eq("approval_status", "rejected")
        .gte("updated_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

      const stats = {
        pending: pendingCount || 0,
        approvedToday: approvedToday || 0,
        rejectedToday: rejectedToday || 0,
      };

      this.logger.info('Approval stats fetched', { organizationId, ...stats });
      return stats;
    }, { operation: 'getApprovalStats', table: 'posts' });
  }
}
